'use client'

import * as React from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { Star } from 'lucide-react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { Marquee } from '@/components/marketing/fragments/marquee'
import { AppInfo } from '@/constants/app-info'
import { cn } from '@/lib/utils'
import { MediaQueries } from '@/constants/media-queries'
import { useMediaQuery } from '@/hooks/use-media-query'

const DATA = [
  {
    name: 'Nguyễn Thị Hồng',
    role: msg`Small-scale trader`,
    img: 'https://randomuser.me/api/portraits/women/65.jpg',
    description: msg`Thanks to ${AppInfo.APP_NAME}'s traceability system, I can easily prove the origin of my goods to customers. <strong>Customers trust me more, and selling has become much easier.</strong>`
  },
  {
    name: 'John Miller',
    role: msg`Digital Marketer - USA`,
    img: 'https://randomuser.me/api/portraits/men/75.jpg',
    description: msg`I joined Pione Group’s NFT minting program and found it <strong>simple and efficient.</strong> The platform works seamlessly even without deep blockchain knowledge.`
  },
  {
    name: 'Lê Mai Anh',
    role: msg`Organic produce store owner`,
    img: 'https://randomuser.me/api/portraits/women/32.jpg',
    description: msg`${AppInfo.APP_NAME} helps me connect directly with customers and track orders more easily. <strong>The app is user-friendly and fully supports Vietnamese.</strong>`
  },
  {
    name: 'Sophie Laurent',
    role: msg`Startup Founder - France`,
    img: 'https://randomuser.me/api/portraits/women/68.jpg',
    description: msg`I’m impressed with Pione’s digital ecosystem, especially the product traceability app. <strong>It has greatly improved my supply chain management efficiency.</strong>`
  },
  {
    name: 'Phạm Minh Hùng',
    role: msg`IT student`,
    img: 'https://randomuser.me/api/portraits/men/77.jpg',
    description: msg`I love how Pione Group applies blockchain in real life. <strong>Minting NFTs for student identity verification is a brilliant idea.</strong>`
  },
  {
    name: 'Anna Schmidt',
    role: msg`Consultant - Germany`,
    img: 'https://randomuser.me/api/portraits/women/45.jpg',
    description: msg`${AppInfo.APP_NAME}'s app is truly convenient for tracking product origins. <strong>I highly value the system’s transparency and ease of use.</strong>`
  },
  {
    name: 'Hoàng Thị Vân',
    role: msg`Housewife`,
    img: 'https://randomuser.me/api/portraits/women/15.jpg',
    description: msg`I used to worry about food of unknown origin. Thanks to ${AppInfo.APP_NAME}'s app, <strong>I can check product information before buying.</strong>`
  },
  {
    name: 'Michael Johnson',
    role: msg`Entrepreneur - Australia`,
    img: 'https://randomuser.me/api/portraits/men/52.jpg',
    description: msg`I used Pione Group’s member NFT minting service to join promotional programs. <strong>It’s really convenient and helps save costs.</strong>`
  },
  {
    name: 'Phan Quốc Tuấn',
    role: msg`Grocery store owner`,
    img: 'https://randomuser.me/api/portraits/men/61.jpg',
    description: msg`Thanks to Pione’s digital payment solution integration, <strong>customers pay faster, and I can manage inventory more easily.</strong>`
  },
  {
    name: 'Emily Clark',
    role: msg`Product Manager - UK`,
    img: 'https://randomuser.me/api/portraits/women/55.jpg',
    description: msg`I appreciate how ${AppInfo.APP_NAME} brings blockchain technology to everyday business use. <strong>Easy to integrate and very user-friendly.</strong>`
  },
  {
    name: 'Lâm Gia Bảo',
    role: msg`Organic farmer`,
    img: 'https://randomuser.me/api/portraits/men/9.jpg',
    description: msg`I use ${AppInfo.APP_NAME}'s traceability system to record farming activities. <strong>Customers know the production process, and my selling prices are better.</strong>`
  }
]

export function Testimonials(): React.JSX.Element {
  const { i18n } = useLingui()
  const isMobile = useMediaQuery(MediaQueries.SmDown, { ssr: true, fallback: true })

  // Giảm số lượng testimonial trên mobile
  const displayData = isMobile ? DATA.slice(0, 4) : DATA
  return (
    <GridSection>
      <div className='sm:container border-x py-20 md:border-none'>
        <h2 className='mb-8 text-center text-3xl font-semibold md:text-5xl lg:text-left'>
          {i18n._('What people say about us')}
        </h2>
        <div className='relative mt-6 max-h-[540px] overflow-hidden'>
          <div className='gap-4 md:columns-2 xl:columns-3 2xl:columns-4'>
            {Array(Math.ceil(displayData.length / 3))
              .fill(0)
              .map((_, i) => (
                <Marquee
                  vertical
                  key={i}
                  className={cn({
                    '[--duration:60s]': i === 1,
                    '[--duration:30s]': i === 2,
                    '[--duration:70s]': i === 3
                  })}
                >
                  {displayData.slice(i * 3, (i + 1) * 3).map((testimonial, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: Math.random() * 0.4,
                        duration: 1
                      }}
                      className='mb-4 flex w-full break-inside-avoid flex-col items-center justify-between gap-6 rounded-xl border bg-background p-4 dark:[border:1px_solid_rgba(255,255,255,.1)] dark:[box-shadow:0_-20px_80px_-20px_#ffffff1f_inset]'
                    >
                      <div className='select-none text-sm font-normal text-muted-foreground'>
                        {i18n._(testimonial.description)}
                        <div className='flex flex-row py-1'>
                          <Star className='size-4 fill-yellow-500 text-yellow-500' />
                          <Star className='size-4 fill-yellow-500 text-yellow-500' />
                          <Star className='size-4 fill-yellow-500 text-yellow-500' />
                          <Star className='size-4 fill-yellow-500 text-yellow-500' />
                          <Star className='size-4 fill-yellow-500 text-yellow-500' />
                        </div>
                      </div>
                      <div className='flex w-full select-none items-center justify-start gap-5'>
                        <Image
                          width={40}
                          height={40}
                          src={testimonial.img || ''}
                          alt={testimonial.name}
                          className='size-8 rounded-full ring-1 ring-border ring-offset-4'
                        />
                        <div>
                          <p className='text-sm font-medium'>{testimonial.name}</p>
                          <p className='text-xs font-normal text-muted-foreground'>
                            {i18n._(testimonial.role)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </Marquee>
              ))}
          </div>
          <div className='pointer-events-none absolute inset-x-0 bottom-0 h-1/4 w-full bg-gradient-to-t from-background from-20%' />
          <div className='pointer-events-none absolute inset-x-0 top-0 h-1/4 w-full bg-gradient-to-b from-background from-20%' />
        </div>
      </div>
    </GridSection>
  )
}
